import React, { Component, ErrorInfo } from "react";
import { withTranslation, WithTranslation } from "react-i18next";

interface Props extends WithTranslation {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): React.ReactNode {
    const { t } = this.props;

    if (this.state.hasError) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "400px",
            padding: "2rem",
            textAlign: "center",
            fontFamily: '"Fira Sans", sans-serif',
          }}
        >
          <h1
            style={{
              fontSize: "1.5rem",
              color: "#1E40AF",
              marginBottom: "1rem",
            }}
          >
            {t("errorBoundary.somethingWentWrong")}
          </h1>
          <p
            style={{
              color: "#64748b",
              marginBottom: "1.5rem",
              maxWidth: "500px",
            }}
          >
            {t("errorBoundary.unexpectedErrorOccurred")}
          </p>
          {process.env.NODE_ENV === "development" && this.state.error && (
            <pre
              style={{
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: "8px",
                padding: "1rem",
                marginBottom: "1.5rem",
                maxWidth: "600px",
                overflow: "auto",
                fontSize: "0.8rem",
                color: "#991b1b",
                textAlign: "left",
              }}
            >
              {this.state.error.message}
            </pre>
          )}
          <div style={{ display: "flex", gap: "0.75rem" }}>
            <button
              onClick={this.handleReset}
              style={{
                padding: "0.5rem 1.5rem",
                background: "#1E40AF",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "0.9rem",
              }}
            >
              {t("errorBoundary.tryAgain")}
            </button>
            <button
              onClick={() => {
                // Class components cannot use React Router hooks.
                // Full reload is intentional to force a clean app state after a crash.
                window.location.href = "/dashboard";
              }}
              style={{
                padding: "0.5rem 1.5rem",
                background: "#f1f5f9",
                color: "#475569",
                border: "1px solid #cbd5e1",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "0.9rem",
              }}
            >
              {t("errorBoundary.goToDashboard")}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default withTranslation("common")(ErrorBoundary);
